import React, {useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Customized,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import Box from '@material-ui/core/Box';
import {ANALYTICS_CHART_COLORS} from 'helpers/constants/analytics';
import {
  hasAnalyticsDateRange,
  resolveAnalyticsRequestDates
} from 'helpers/services/api/analyticsHelpers';
import {useAnalyticsFilters} from 'components/AnalyticsFilters/AnalyticsFiltersContext';
import roundTheNumber from '../../../helpers/functionUtils/roundTheNumber';
import {GET_SALES_PER_HOUR} from '../../../store/analyticsSalesPerHour/action';
import {SauceChart} from '../../../routes/App/dashboard/styles';
import {useDatePickerContext} from '../../Datepicker/DatePickerContext';
import Loading from '../../shared/Utils/Loading';

const BOUNDARY_LABELS = [
  {id: 'b00', value: 12},
  {id: 'b01', value: 1},
  {id: 'b02', value: 2},
  {id: 'b03', value: 3},
  {id: 'b04', value: 4},
  {id: 'b05', value: 5},
  {id: 'b06', value: 6},
  {id: 'b07', value: 7},
  {id: 'b08', value: 8},
  {id: 'b09', value: 9},
  {id: 'b10', value: 10},
  {id: 'b11', value: 11},
  {id: 'b12', value: 12},
  {id: 'b13', value: 1},
  {id: 'b14', value: 2},
  {id: 'b15', value: 3},
  {id: 'b16', value: 4},
  {id: 'b17', value: 5},
  {id: 'b18', value: 6},
  {id: 'b19', value: 7},
  {id: 'b20', value: 8},
  {id: 'b21', value: 9},
  {id: 'b22', value: 10},
  {id: 'b23', value: 11},
  {id: 'b24', value: 12}
];

const BoundaryLabels = ({offset, height}) => {
  if (!offset || !height) return null;
  const N = BOUNDARY_LABELS.length;
  const y = height - 15;
  return (
    <g>
      {BOUNDARY_LABELS.map((label, i) => {
        const x = offset.left + (i / (N - 1)) * offset.width;
        let textAnchor = 'middle';
        if (i === 0) textAnchor = 'start';
        else if (i === N - 1) textAnchor = 'end';
        return (
          <text
            key={label.id}
            x={x}
            y={y}
            textAnchor={textAnchor}
            fill='#666'
            fontSize={16}
          >
            {label.value}
          </text>
        );
      })}
    </g>
  );
};

BoundaryLabels.propTypes = {
  offset: PropTypes.shape({
    left: PropTypes.number,
    width: PropTypes.number
  }),
  height: PropTypes.number
};

BoundaryLabels.defaultProps = {
  offset: null,
  height: 0
};

const SalesByHours = ({
  grouping,
  disableFetch,
  dataOverride,
  seriesOverride,
  avgSalesOverride,
  loadingOverride
}) => {
  const dispatch = useDispatch();
  const {channelTypes, fulfillmentModes} = useAnalyticsFilters();
  const {locationId} = useSelector((state) => state.user);
  const {salesPerHourLoading} = useSelector(
    (state) => state.analyticsSalesPerHourReducer
  );
  const group = useSelector(
    (state) => state.analyticsSalesPerHourReducer[grouping]
  );

  const date = useDatePickerContext();

  useEffect(() => {
    if (disableFetch || !locationId || !hasAnalyticsDateRange(date)) {
      return;
    }
    const {startDate, endDate} = resolveAnalyticsRequestDates(date);
    dispatch(
      GET_SALES_PER_HOUR({
        startDate,
        endDate,
        channelTypes,
        fulfillmentModes
      })
    );
  }, [
    date,
    locationId,
    dispatch,
    channelTypes,
    fulfillmentModes,
    disableFetch
  ]);

  const resolvedHours = dataOverride ?? group?.hours;
  const resolvedSeries = seriesOverride ?? group?.series ?? [];
  const resolvedAvgSales = avgSalesOverride ?? group?.avgSales;
  const resolvedLoading = loadingOverride ?? salesPerHourLoading;

  const chartData = useMemo(() => {
    const toDisplay = (hour) => {
      if (hour === 0 || hour === 24) return 12;
      if (hour > 12) return hour - 12;
      return hour;
    };

    const graph = new Array(24).fill().map((_, i) => ({
      rawHour: i,
      name: toDisplay(i),
      avgSales: resolvedAvgSales ?? 0,
      info: `${toDisplay(i)} - ${toDisplay(i + 1)}`
    }));

    if (resolvedHours?.length) {
      resolvedHours.forEach((row) => {
        const hourIndex = Number(row.name);
        if (hourIndex >= 0 && hourIndex < 24) {
          const seriesValues = {};
          resolvedSeries.forEach((entry) => {
            seriesValues[entry.key] = Number(row[entry.key]) || 0;
          });
          graph[hourIndex] = {
            ...graph[hourIndex],
            ...seriesValues
          };
        }
      });
    }

    return graph;
  }, [resolvedHours, resolvedSeries, resolvedAvgSales]);

  const isStacked = resolvedSeries.length > 1;

  const CustomTooltip = ({active, payload}) => {
    if (!active || !payload?.length) {
      return null;
    }
    const row = payload[0].payload;
    return (
      <Box className='ChartTooltip'>
        <p>{row.info}</p>
        {resolvedSeries.map((entry) => (
          <p key={entry.key}>
            {`${entry.label}: $${roundTheNumber(row[entry.key] ?? 0)}`}
          </p>
        ))}
        <p>{`Avg Sales: $${roundTheNumber(row.avgSales)}`}</p>
      </Box>
    );
  };

  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.object)
  };

  CustomTooltip.defaultProps = {
    active: false,
    payload: []
  };

  return (
    <>
      {resolvedLoading && <Loading />}

      {!resolvedLoading && (
        <SauceChart>
          <div>
            <ResponsiveContainer>
              <ComposedChart data={chartData}>
                <defs>
                  <pattern
                    id={`avgSalesHatch-${grouping}`}
                    patternUnits='userSpaceOnUse'
                    width='6'
                    height='6'
                    patternTransform='rotate(45)'
                  >
                    <rect width='6' height='6' fill='#fff' />
                    <line
                      x1='0'
                      y1='0'
                      x2='0'
                      y2='6'
                      stroke={ANALYTICS_CHART_COLORS.avgLine}
                      strokeWidth='2'
                    />
                  </pattern>
                </defs>
                <CartesianGrid
                  strokeDasharray='0'
                  strokeWidth='1'
                  stroke='#E7E7E7'
                  vertical={false}
                />
                <XAxis
                  dataKey='rawHour'
                  axisLine={false}
                  tickLine={false}
                  tick={false}
                  scale='band'
                />
                <YAxis axisLine={false} tickLine={false} tick={{dx: 0}} />
                <Tooltip
                  cursor={{fill: 'rgba(23,201,129, .1)'}}
                  content={<CustomTooltip />}
                />
                {resolvedSeries.map((entry, index) => (
                  <Bar
                    key={entry.key}
                    dataKey={entry.key}
                    stackId={isStacked ? 'sales' : undefined}
                    fill={entry.color}
                    radius={
                      !isStacked || index === resolvedSeries.length - 1
                        ? [6, 6, 0, 0]
                        : [0, 0, 0, 0]
                    }
                    barSize={20}
                  />
                ))}
                <ReferenceLine
                  y={chartData[0]?.avgSales ?? 0}
                  stroke={`url(#avgSalesHatch-${grouping})`}
                  strokeWidth={2}
                  ifOverflow='extendDomain'
                />
                <Customized component={BoundaryLabels} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </SauceChart>
      )}
    </>
  );
};

SalesByHours.defaultProps = {
  grouping: 'perFulfillment',
  disableFetch: false,
  dataOverride: null,
  seriesOverride: null,
  avgSalesOverride: undefined,
  loadingOverride: undefined
};

SalesByHours.propTypes = {
  grouping: PropTypes.oneOf(['perFulfillment', 'perChannel']),
  disableFetch: PropTypes.bool,
  dataOverride: PropTypes.arrayOf(PropTypes.object),
  seriesOverride: PropTypes.arrayOf(PropTypes.object),
  avgSalesOverride: PropTypes.number,
  loadingOverride: PropTypes.bool
};

export default SalesByHours;
